import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowRight, X, Sparkles, CheckCircle } from 'lucide-react';
import { useOnboarding } from '../../context/OnboardingContext';
import { Button } from '../ui/button';
import { isAhaComplete, subscribe } from '../../data/demoStore';

export function SetupBanner() {
  const { isSetupComplete, bannerDismissed, dismissBanner, getSetupPercentage, getCompletedStepsCount, requiredStepsCount } = useOnboarding();
  const [aha, setAha] = useState(isAhaComplete());
  const [ahaBannerDismissed, setAhaBannerDismissed] = useState(() => {
    try {
      return localStorage.getItem('vedaya_aha_banner_dismissed') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    return subscribe(() => {
      setAha(isAhaComplete());
    });
  }, []);

  const dismissAhaBanner = () => {
    try {
      localStorage.setItem('vedaya_aha_banner_dismissed', 'true');
    } catch {}
    setAhaBannerDismissed(true);
  };

  // Post-aha celebration banner
  if (aha && !ahaBannerDismissed) {
    return (
      <div
        className="bg-green-50 border-b border-green-200 px-4 py-3"
        role="alert"
        aria-live="polite"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center shrink-0">
              <CheckCircle className="w-4 h-4 text-green-600" aria-hidden="true" />
            </div>
            <div>
              <p className="text-sm font-medium text-green-900">
                You just completed your first IT workflow with Vedaya!
              </p>
              <p className="text-xs text-green-700">
                Explore advanced features or continue handling requests.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild size="sm" variant="outline" className="border-green-300 text-green-700 hover:bg-green-100">
              <Link to="/control-tower">
                <Sparkles className="w-4 h-4 mr-1" aria-hidden="true" />
                Explore Control Tower
              </Link>
            </Button>
            <button
              onClick={dismissAhaBanner}
              className="p-1.5 rounded-md hover:bg-green-100 transition-colors text-green-500 hover:text-green-700"
              aria-label="Dismiss celebration banner"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Hide normal setup banner pre-aha - only show post-aha celebration
  // Pre-aha users should focus on the demo flow, not setup
  if (!aha) {
    return null;
  }

  // Normal setup banner (only shown post-aha if setup incomplete)
  if (isSetupComplete() || bannerDismissed) {
    return null;
  }

  const percentage = getSetupPercentage();
  const completedSteps = getCompletedStepsCount();

  return (
    <div
      className="bg-slate-100 border-b border-slate-200 px-4 py-3"
      role="alert"
      aria-live="polite"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
            <AlertCircle className="w-4 h-4 text-slate-600" aria-hidden="true" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900">
              Setup incomplete ({completedSteps}/{requiredStepsCount} steps · {percentage}%)
            </p>
            <p className="text-xs text-slate-600">
              Finish onboarding to enable ticket intake and automation.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild size="sm" variant="default">
            <Link to="/setup">
              Continue setup
              <ArrowRight className="w-4 h-4 ml-1" aria-hidden="true" />
            </Link>
          </Button>
          <button
            onClick={dismissBanner}
            className="p-1.5 rounded-md hover:bg-slate-200 transition-colors text-slate-500 hover:text-slate-700"
            aria-label="Dismiss setup banner"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default SetupBanner;
